import { useState, useEffect } from "react";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Cigarette, PawPrint, PartyPopper, Baby, Clock, Volume2, Plus, X, KeyRound, User, MapPin } from "lucide-react";

const DEFAULT_RULES = {
  smoking: false, pets: false, parties: false, children: true,
  check_in_from: "15:00", check_in_to: "20:00", check_out_by: "10:00",
  quiet_from: "22:00", quiet_to: "08:00",
  custom: [],
};

const DEFAULT_ACCESS = {
  type: "in_person",
  contact_name: "", contact_phone: "", meeting_point: "",
  self_code: "", self_lockbox_location: "", self_photo: "",
  notes: "",
};

function BoolPill({ active, onClick, icon: Icon, label, negative = false, testid }) {
  const activeColor = negative ? "bg-destructive/10 border-destructive text-destructive" : "bg-sage/10 border-sage text-sage";
  return (
    <button type="button" onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all ${active ? activeColor + " font-medium shadow-sm" : "bg-card border-border text-muted-foreground hover:border-primary/40"}`}
      data-testid={testid}>
      <Icon className="w-3.5 h-3.5"/>{label}
    </button>
  );
}

export default function HouseRulesAndAccess({ property, onSave }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const [rules, setRules] = useState({ ...DEFAULT_RULES, ...(property?.house_rules || {}) });
  const [access, setAccess] = useState({ ...DEFAULT_ACCESS, ...(property?.access_instructions || {}) });
  const [customInput, setCustomInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRules({ ...DEFAULT_RULES, ...(property?.house_rules || {}) });
    setAccess({ ...DEFAULT_ACCESS, ...(property?.access_instructions || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.id]);

  const toggle = (k) => setRules((r) => ({ ...r, [k]: !r[k] }));
  const addCustom = () => {
    const c = customInput.trim();
    if (!c) return;
    setRules((r) => ({ ...r, custom: [...(r.custom || []), c] }));
    setCustomInput("");
  };
  const removeCustom = (i) => setRules((r) => ({ ...r, custom: r.custom.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    await onSave({ house_rules: rules, access_instructions: access });
    setSaving(false);
  };

  return (
    <div className="space-y-8 border-t border-border pt-6" data-testid="house-rules-and-access">
      {/* HOUSE RULES */}
      <div>
        <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-1">
          {IT ? "Regole della casa" : "House rules"}
        </div>
        <h3 className="font-display text-2xl mb-4">{IT ? "Cosa è consentito nella struttura" : "What's allowed on-site"}</h3>

        <div className="flex flex-wrap gap-2 mb-5" data-testid="rules-toggles">
          <BoolPill testid="rule-smoking" active={rules.smoking} onClick={() => toggle("smoking")} icon={Cigarette}
            label={rules.smoking ? (IT ? "Fumo consentito" : "Smoking allowed") : (IT ? "Vietato fumare" : "No smoking")}
            negative={!rules.smoking}/>
          <BoolPill testid="rule-pets" active={rules.pets} onClick={() => toggle("pets")} icon={PawPrint}
            label={rules.pets ? (IT ? "Animali ammessi" : "Pets allowed") : (IT ? "No animali" : "No pets")}
            negative={!rules.pets}/>
          <BoolPill testid="rule-parties" active={rules.parties} onClick={() => toggle("parties")} icon={PartyPopper}
            label={rules.parties ? (IT ? "Feste ammesse" : "Parties allowed") : (IT ? "No feste/eventi" : "No parties/events")}
            negative={!rules.parties}/>
          <BoolPill testid="rule-children" active={rules.children} onClick={() => toggle("children")} icon={Baby}
            label={rules.children ? (IT ? "Bambini benvenuti" : "Kids welcome") : (IT ? "Solo adulti" : "Adults only")}
            negative={!rules.children}/>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3"/>{IT ? "Check-in dalle" : "Check-in from"}</Label>
            <Input type="time" value={rules.check_in_from} onChange={(e) => setRules({ ...rules, check_in_from: e.target.value })} data-testid="rule-checkin-from"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Check-in entro" : "Check-in by"}</Label>
            <Input type="time" value={rules.check_in_to} onChange={(e) => setRules({ ...rules, check_in_to: e.target.value })} data-testid="rule-checkin-to"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Check-out entro" : "Check-out by"}</Label>
            <Input type="time" value={rules.check_out_by} onChange={(e) => setRules({ ...rules, check_out_by: e.target.value })} data-testid="rule-checkout-by"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Volume2 className="w-3 h-3"/>{IT ? "Silenzio dalle" : "Quiet hours"}</Label>
            <Input type="time" value={rules.quiet_from} onChange={(e) => setRules({ ...rules, quiet_from: e.target.value })} data-testid="rule-quiet-from"/>
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Regole personalizzate" : "Custom rules"}</Label>
          <div className="flex gap-2">
            <Input value={customInput} onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); }}}
              placeholder={IT ? "Es. Rimuovere le scarpe all'ingresso" : "e.g. Please remove shoes at entrance"}
              data-testid="rule-custom-input"/>
            <Button type="button" onClick={addCustom} variant="outline" data-testid="rule-custom-add">
              <Plus className="w-4 h-4"/>
            </Button>
          </div>
          {rules.custom?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {rules.custom.map((r, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-muted border border-border" data-testid={`rule-custom-${i}`}>
                  {r}
                  <button type="button" onClick={() => removeCustom(i)} className="hover:text-destructive"><X className="w-3 h-3"/></button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ACCESS INSTRUCTIONS */}
      <div>
        <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-1">
          {IT ? "Istruzioni per l'accesso" : "Access instructions"}
        </div>
        <h3 className="font-display text-2xl mb-1">{IT ? "Come arriva l'ospite" : "How the guest arrives"}</h3>
        <p className="text-xs text-muted-foreground mb-4">
          {IT ? "Visibili al guest solo dopo la conferma della prenotazione." : "Shown to guest only after booking confirmation."}
        </p>

        <RadioGroup value={access.type} onValueChange={(v) => setAccess({ ...access, type: v })} className="grid sm:grid-cols-2 gap-3 mb-5">
          <label className={`border rounded-lg p-4 cursor-pointer transition-all ${access.type === "in_person" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`} data-testid="access-type-in_person">
            <div className="flex items-start gap-3">
              <RadioGroupItem value="in_person" id="in-person"/>
              <div className="flex-1">
                <div className="font-medium flex items-center gap-2"><User className="w-4 h-4 text-primary"/>{IT ? "Check-in di persona" : "In-person check-in"}</div>
                <div className="text-xs text-muted-foreground mt-1">{IT ? "Un membro dello staff accoglie l'ospite" : "A staff member welcomes the guest"}</div>
              </div>
            </div>
          </label>
          <label className={`border rounded-lg p-4 cursor-pointer transition-all ${access.type === "self" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`} data-testid="access-type-self">
            <div className="flex items-start gap-3">
              <RadioGroupItem value="self" id="self"/>
              <div className="flex-1">
                <div className="font-medium flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary"/>{IT ? "Self check-in" : "Self check-in"}</div>
                <div className="text-xs text-muted-foreground mt-1">{IT ? "Cassaforte chiavi, codice porta, keybox" : "Key safe, door code, keybox"}</div>
              </div>
            </div>
          </label>
        </RadioGroup>

        {access.type === "in_person" ? (
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Nome referente" : "Contact name"}</Label>
              <Input value={access.contact_name} onChange={(e) => setAccess({ ...access, contact_name: e.target.value })} placeholder={IT ? "Mario Rossi" : "John Doe"} data-testid="access-contact-name"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Telefono referente" : "Contact phone"}</Label>
              <Input value={access.contact_phone} onChange={(e) => setAccess({ ...access, contact_phone: e.target.value })} placeholder="+39 333 1234567" data-testid="access-contact-phone"/>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3"/>{IT ? "Punto d'incontro" : "Meeting point"}</Label>
              <Input value={access.meeting_point} onChange={(e) => setAccess({ ...access, meeting_point: e.target.value })}
                placeholder={IT ? "Davanti alla porta d'ingresso, citofono 3" : "Front door, buzzer 3"} data-testid="access-meeting-point"/>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Codice porta / cassaforte" : "Door / safe code"}</Label>
              <Input value={access.self_code} onChange={(e) => setAccess({ ...access, self_code: e.target.value })} placeholder="1234" className="font-mono tracking-widest" data-testid="access-self-code"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Posizione della keybox" : "Keybox location"}</Label>
              <Input value={access.self_lockbox_location} onChange={(e) => setAccess({ ...access, self_lockbox_location: e.target.value })}
                placeholder={IT ? "Cancello di destra, sopra il citofono" : "Right gate, above intercom"} data-testid="access-self-lockbox"/>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Foto della posizione (URL)" : "Location photo (URL)"}</Label>
              <Input value={access.self_photo} onChange={(e) => setAccess({ ...access, self_photo: e.target.value })} placeholder="https://…" data-testid="access-self-photo"/>
            </div>
          </div>
        )}

        <div className="mt-3">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Note aggiuntive per l'ospite" : "Additional notes for the guest"}</Label>
          <Textarea rows={3} value={access.notes} onChange={(e) => setAccess({ ...access, notes: e.target.value })}
            placeholder={IT ? "Es. Parcheggio gratuito in Via Roma 12. WiFi: 'CasaAmalfi_5G' password 'amalfi2026'." : "e.g. Free parking on Via Roma 12. Wi-Fi 'MyProperty' password 'guest2026'."}
            data-testid="access-notes"/>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="rules-access-save">
          {saving ? "…" : (IT ? "Salva regole e istruzioni" : "Save rules & instructions")}
        </Button>
      </div>
    </div>
  );
}
